<template>
  <div class="flex gap-2 sticky-navigation">
    <div class="sidebar-control-wrapper">
      <VaIconMenuCollapsed
        class="cursor-pointer"
        :class="{ 'x-flip': !isSidebarMinimized }"
        :color="collapseIconColor"
        :auto-collapse="autoCollapseEnabled"
        :title="
          autoCollapseEnabled
            ? 'Click to toggle sidebar | Click A/M to switch mode'
            : 'Click to toggle sidebar | Click A/M to switch mode'
        "
        @click="handleIconClick"
        @toggleMode="toggleAutoCollapse"
      />
    </div>

    <nav class="flex items-center">
      <VaBreadcrumbs>
        <VaBreadcrumbsItem label="Home" :to="{ name: 'wim' }" /> //was 'dashboard'
        <VaBreadcrumbsItem
          v-for="item in items"
          :key="item.label"
          :label="item.label"
          @click="handleBreadcrumbClick(item)"
        />
      </VaBreadcrumbs>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import VaIconMenuCollapsed from '../icons/VaIconMenuCollapsed.vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import NavigationRoutes from '../sidebar/NavigationRoutes'

const globalStore = useGlobalStore()
const { isSidebarMinimized, autoCollapseEnabled } = storeToRefs(globalStore)

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
}

const findRouteName = (name: string) => {
  const traverse = (routers: any[]): string => {
    for (const router of routers) {
      if (router.name === name) {
        return router.displayName
      }
      if (router.children) {
        const result = traverse(router.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }

  return traverse(NavigationRoutes.routes)
}

const items = computed(() => {
  const result: { label: string; to: string; hasChildren: boolean }[] = []
  route.matched.forEach((route) => {
    const labelKey = findRouteName(route.name as string)
    if (!labelKey) {
      return
    }
    result.push({
      label: t(labelKey),
      to: route.path,
      hasChildren: route.children && route.children.length > 0,
    })
  })
  return result
})

const { getColor } = useColors()

const collapseIconColor = computed(() => getColor('secondary'))

const handleBreadcrumbClick = (item: BreadcrumbNavigationItem) => {
  if (!item.hasChildren) {
    router.push(item.to)
  }
}

const handleIconClick = () => {
  // Toggle the sidebar state
  // The sidebar component will handle syncing based on auto-collapse mode
  isSidebarMinimized.value = !isSidebarMinimized.value
}

const toggleAutoCollapse = () => {
  globalStore.toggleAutoCollapse()
}
</script>

<style lang="scss" scoped>
.sticky-navigation {
  position: -webkit-sticky; /* Safari support */
  position: sticky;
  top: 0;
  z-index: 101; /* Above cards (z-index: 100) but below modals (z-index: 1000) */
  background-color: var(--va-background-primary);
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
}

.sidebar-control-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.x-flip {
  transform: scaleX(-100%);
}
</style>
